/**
 * App.tsx — Main application shell.
 *
 * Business logic lives in dedicated hooks:
 *   - useAppLifecycle: Auth, SW updates, version polling, maintenance
 *   - useSessionActions: Session/game callbacks
 *
 * Tab views and overlays are separate components:
 *   - ExamTab, BottomTabBar, PremiumCelebration
 */
import React from 'react';
import { useUIStore, useUserStore } from './stores';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import './i18n';
import { CloudOff, History, Loader2, Shield, Sparkles } from 'lucide-react';

import {
  FahryoLogo,
  LegalModal,
  OnboardingModal,
  BadgeModal,
  ConsentBanner,
} from './components/UIComponents';
const PremiumModal = React.lazy(() => import('./components/PremiumModal'));
const WhatsNewModal = React.lazy(() => import('./components/WhatsNewModal'));
const AnnouncementBanner = React.lazy(() => import('./components/AnnouncementBanner'));
import { ApiClient } from './services/apiClient';
import { supabase } from './services/supabaseClient';
import {
  loadSessionSnapshot,
  clearSessionSnapshot,
  type SessionSnapshot,
} from './services/sessionSnapshot';
import type { Question } from '../shared/types';

// Code-split page components
const AuthView = React.lazy(() => import('./pages/AuthView'));
const LearnView = React.lazy(() => import('./pages/LearnView'));
const QuestionSession = React.lazy(() => import('./pages/QuestionSession'));
const ExamResultView = React.lazy(() => import('./pages/ExamResultView'));
const LeaderboardView = React.lazy(() => import('./pages/LeaderboardView'));
const ProfileView = React.lazy(() => import('./pages/ProfileView'));
const ChatView = React.lazy(() => import('./pages/ChatView'));

import { BetaFeedbackModal } from './components/BetaFeedbackModal';
import InstallPrompt from './components/InstallPrompt';
import PageErrorBoundary from './components/PageErrorBoundary';
import ConfidenceCheck from './components/ConfidenceCheck';
import ExamTab from './components/ExamTab';
import PremiumCelebration from './components/PremiumCelebration';
import BottomTabBar from './components/BottomTabBar';
import {
  LearnViewSkeleton,
  LeaderboardSkeleton,
  ChatSkeleton,
  ProfileSkeleton,
} from './components/Skeletons';
import { ToastContainer } from './components/Toast';
const PlacementResult = React.lazy(() => import('./components/learn/PlacementResult'));
const DevTools = React.lazy(() => import('./components/DevTools'));

// Consolidated hooks
import { useAppLifecycle } from './hooks/useAppLifecycle';
import { useSessionActions } from './hooks/useSessionActions';

declare const __APP_VERSION__: string;

const App: React.FC = () => {
  const { t } = useTranslation();

  // ── Zustand Stores ──
  const activeTab = useUIStore((s) => s.activeTab);
  const tabDirection = useUIStore((s) => s.tabDirection);
  const showPaywall = useUIStore((s) => s.showPaywall);
  const showOnboarding = useUIStore((s) => s.showOnboarding);
  const showBetaFeedback = useUIStore((s) => s.showBetaFeedback);
  const showConfidenceCheck = useUIStore((s) => s.showConfidenceCheck);
  const showPremiumCelebration = useUIStore((s) => s.showPremiumCelebration);
  const legalModalType = useUIStore((s) => s.legalModalType);
  const premiumTrigger = useUIStore((s) => s.premiumTrigger);
  const premiumContext = useUIStore((s) => s.premiumContext);
  const toasts = useUIStore((s) => s.toasts);
  const dismissToast = useUIStore((s) => s.dismissToast);
  const showUpdateToast = useUIStore((s) => s.showUpdateToast);
  const updatePromptVisible = useUIStore((s) => s.updatePromptVisible);
  const updateApply = useUIStore((s) => s.updateApply);
  const setUpdatePrompt = useUIStore((s) => s.setUpdatePrompt);
  const isOffline = useUIStore((s) => s.isOffline);
  const isLoadingQuestions = useUIStore((s) => s.isLoadingQuestions);
  const authLoading = useUIStore((s) => s.authLoading);
  const maintenanceMode = useUIStore((s) => s.maintenanceMode);
  const showBetaBadge = useUIStore((s) => s.showBetaBadge);
  const betaBannerMeta = useUIStore((s) => s.betaBannerMeta);

  const user = useUserStore((s) => s.user);
  const sessionData = useUserStore((s) => s.sessionData);
  const examResult = useUserStore((s) => s.examResult);
  const placementResult = useUserStore((s) => s.placementResult);
  const newBadge = useUserStore((s) => s.newBadge);

  const uiActions = useUIStore.getState();
  const userActions = useUserStore.getState();

  // ── Hooks ──
  useAppLifecycle();
  const {
    handleLogin,
    handleLogout,
    startChapter,
    startMistakes,
    startSrs,
    startSmartSession,
    startExamWithConfidenceCheck,
    startExam,
    startHardTraining,
    startPlacementTest,
    handleSessionComplete,
    handleUpgrade: _handleUpgrade,
    handleConsent,
  } = useSessionActions();

  const isPremium = user?.isPremium === true;

  // ── Session-Resume (F-09) ──
  // After a deploy reload, PWA kill or crash: offer to pick the
  // interrupted session back up instead of silently dropping it.
  const [resumeSnapshot, setResumeSnapshot] = React.useState<SessionSnapshot | null>(null);
  React.useEffect(() => {
    if (!user || sessionData || isLoadingQuestions || authLoading) return;
    const snap = loadSessionSnapshot();
    if (!snap) return;
    const byId = new Map(ApiClient.getAllQuestions().map((q) => [q.id, q]));
    if (snap.questionIds.some((id) => !byId.has(id))) {
      // Question set changed underneath the snapshot (content deploy) —
      // resuming would corrupt indices, so drop it.
      clearSessionSnapshot();
      return;
    }
    setResumeSnapshot(snap);
  }, [user, sessionData, isLoadingQuestions, authLoading]);

  const handleResumeSession = () => {
    if (!resumeSnapshot) return;
    const byId = new Map(ApiClient.getAllQuestions().map((q) => [q.id, q]));
    const resumedQuestions = resumeSnapshot.questionIds
      .map((id) => byId.get(id))
      .filter((q): q is Question => q !== undefined);
    setResumeSnapshot(null);
    if (resumedQuestions.length !== resumeSnapshot.questionIds.length) {
      clearSessionSnapshot();
      return;
    }
    userActions.setSessionData({
      questions: resumedQuestions,
      mode: resumeSnapshot.mode,
      chapterId: resumeSnapshot.chapterId,
      isPreview: resumeSnapshot.isPreview,
      resume: {
        index: Math.min(resumeSnapshot.currentIndex, resumedQuestions.length - 1),
        answers: resumeSnapshot.answers,
        submitted: resumeSnapshot.submitted,
        examTimeLeft: resumeSnapshot.examTimeLeft,
      },
    });
  };

  const handleDiscardSession = () => {
    clearSessionSnapshot();
    setResumeSnapshot(null);
  };

  // ── Early Returns ──

  if (maintenanceMode) {
    return (
      <div className="min-h-screen bg-transparent flex flex-col items-center justify-center p-6">
        <FahryoLogo tile tileClassName="mb-8" />
        <div className="glass-strong text-center max-w-sm p-6">
          <div className="glass-pill w-16 h-16 flex items-center justify-center mb-4 mx-auto !bg-orange-100/70 dark:!bg-orange-900/30">
            <Shield size={32} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--f-text)] mb-3">{t('maintenance_title')}</h1>
          <p className="text-[var(--f-text-muted)] mb-6">{t('Wartungsarbeiten')}</p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
            <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
            {t('maintenance_working')}
          </div>
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-transparent">
        <div className="w-12 h-12 border-4 border-[var(--f-border)] border-t-primary rounded-full animate-spin mb-4" />
        <p className="text-[var(--f-text-muted)] text-sm">{t('app_loading')}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <PageErrorBoundary pageName="AuthView">
          <React.Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="animate-spin text-primary" size={32} />
              </div>
            }
          >
            <AuthView onLogin={handleLogin} onOpenLegal={uiActions.setLegalModalType} />
          </React.Suspense>
        </PageErrorBoundary>
        <LegalModal
          isOpen={!!legalModalType}
          type={legalModalType}
          onClose={() => uiActions.setLegalModalType(null)}
        />
      </>
    );
  }

  if (showOnboarding) {
    return (
      <div className="max-w-lg sm:max-w-xl lg:max-w-2xl mx-auto min-h-screen bg-transparent relative shadow-2xl">
        <OnboardingModal
          isOpen={true}
          userName={user.name !== 'Fahrschüler' ? user.name : undefined}
          onFinish={(updates) => {
            if (updates.language && updates.language !== i18n.language) {
              i18n.changeLanguage(updates.language);
              window.dispatchEvent(
                new CustomEvent('fahryo-language-changed', {
                  detail: { language: updates.language },
                })
              );
            }
            uiActions.setShowOnboarding(false);
            const updatedUser = ApiClient.updateUser({ hasSeenOnboarding: true, ...updates });
            userActions.setUser(updatedUser);
            (async () => {
              try {
                const {
                  data: { session: s },
                } = await supabase.auth.getSession();
                if (!s) return;
                const apiBase =
                  window.location.hostname === 'localhost'
                    ? 'http://localhost:3000'
                    : 'https://app.fahryo.de';
                await fetch(`${apiBase}/api/email/trigger/onboarding`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${s.access_token}`,
                  },
                });
              } catch {
                /* fire-and-forget */
              }
            })();
          }}
        />
      </div>
    );
  }

  if (isLoadingQuestions) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-transparent">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mb-4"></div>
        <p className="text-[var(--f-text-muted)]">{t('App wird geladen...')}</p>
      </div>
    );
  }

  if (examResult) {
    return (
      <div className="max-w-lg sm:max-w-xl lg:max-w-2xl mx-auto min-h-screen bg-transparent shadow-2xl">
        <PageErrorBoundary
          pageName="ExamResultView"
          onReset={() => userActions.setExamResult(null)}
        >
          <ExamResultView
            result={examResult}
            onContinue={() => userActions.setExamResult(null)}
            onPracticeChapter={(chapterId) => {
              userActions.setExamResult(null);
              userActions.setSessionData({
                questions: ApiClient.getQuestionsForChapter(chapterId),
                mode: 'learn',
                chapterId,
              });
            }}
          />
        </PageErrorBoundary>
      </div>
    );
  }

  if (placementResult) {
    return (
      <div className="max-w-lg sm:max-w-xl lg:max-w-2xl mx-auto min-h-screen bg-transparent shadow-2xl">
        <PageErrorBoundary
          pageName="PlacementResult"
          onReset={() => userActions.setPlacementResult(null)}
        >
          <React.Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin w-8 h-8 border-3 border-primary border-t-transparent rounded-full" />
              </div>
            }
          >
            <PlacementResult
              results={placementResult.chapterResults}
              totalCorrect={placementResult.totalCorrect}
              totalQuestions={placementResult.totalQuestions}
              onStartChapter={(chapterId) => {
                userActions.setPlacementResult(null);
                userActions.setSessionData({
                  questions: ApiClient.getQuestionsForChapter(chapterId),
                  mode: 'learn',
                  chapterId,
                });
              }}
              onContinue={() => userActions.setPlacementResult(null)}
            />
          </React.Suspense>
        </PageErrorBoundary>
      </div>
    );
  }

  if (sessionData) {
    return (
      <div className="max-w-lg sm:max-w-xl lg:max-w-2xl mx-auto min-h-screen bg-transparent shadow-2xl">
        <PageErrorBoundary
          pageName="QuestionSession"
          onReset={() => userActions.setSessionData(null)}
        >
          <QuestionSession
            questions={sessionData.questions}
            onComplete={handleSessionComplete}
            user={user}
            mode={sessionData.mode}
            initialIndex={sessionData.resume?.index}
            initialAnswers={sessionData.resume?.answers}
            initialSubmitted={sessionData.resume?.submitted}
            initialExamTimeLeft={sessionData.resume?.examTimeLeft}
            onExit={() => userActions.setSessionData(null)}
            onToggleBookmark={(id) => {
              ApiClient.toggleBookmark(id);
            }}
          />
        </PageErrorBoundary>
      </div>
    );
  }

  // ── Main App Layout ──
  return (
    <div className="max-w-lg sm:max-w-xl lg:max-w-2xl mx-auto min-h-screen bg-transparent relative shadow-2xl overflow-x-hidden transition-colors safe-top safe-bottom">
      {isOffline && (
        <div className="bg-amber-500 text-white text-center py-2 px-4 text-xs font-medium flex items-center justify-center gap-2 animate-in slide-in-from-top duration-300">
          <CloudOff size={14} />
          <span>{t('Kein Internet — Deine Fortschritte werden lokal gespeichert')}</span>
        </div>
      )}

      {/* AUTH-FIX v2: WhatsNew only for users who completed onboarding.
          New users (or minimal-user from DB timeout) have lastSeenWhatsNew=undefined
          which causes a since=0.0.0 API call returning all entries. */}
      {user &&
        !showOnboarding &&
        !showPremiumCelebration &&
        user.consentGiven !== null &&
        user.hasSeenOnboarding &&
        !premiumTrigger && (
          <React.Suspense fallback={null}>
            <WhatsNewModal
              currentVersion={typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0'}
              lastSeenVersion={user.lastSeenWhatsNew}
              onDismiss={(version) =>
                userActions.setUser(ApiClient.updateUser({ lastSeenWhatsNew: version }))
              }
              onNavigate={(tab) => uiActions.setActiveTab(tab as any)}
            />
          </React.Suspense>
        )}

      {user &&
        !showOnboarding &&
        !showPremiumCelebration &&
        user.consentGiven !== null &&
        !premiumTrigger && (
          <React.Suspense fallback={null}>
            <AnnouncementBanner
              isPremium={user.isPremium}
              onNavigate={(tab) => uiActions.setActiveTab(tab as any)}
            />
          </React.Suspense>
        )}

      <div className="h-full overflow-y-auto no-scrollbar">
        <div
          key={activeTab}
          className={`animate-in ${tabDirection === 'right' ? 'slide-in-from-right-2' : 'slide-in-from-left-2'} fade-in duration-200 pb-28`}
        >
          {activeTab === 'learn' && (
            <PageErrorBoundary pageName="LearnView">
              <React.Suspense fallback={<LearnViewSkeleton />}>
                <LearnView
                  user={user}
                  isPremium={isPremium}
                  showBetaBadge={showBetaBadge}
                  betaBannerMeta={betaBannerMeta}
                  onSelectChapter={startChapter}
                  onStartMistakes={startMistakes}
                  onStartSrs={startSrs}
                  onStartBookmarks={() => {
                    const bookmarkedQs = ApiClient.getAllQuestions().filter((q) =>
                      user.bookmarkedQuestions.includes(q.id)
                    );
                    if (bookmarkedQs.length > 0)
                      userActions.setSessionData({ questions: bookmarkedQs, mode: 'bookmarks' });
                  }}
                  onStartSmartSession={startSmartSession}
                  onStartExam={startExamWithConfidenceCheck}
                  onStartHardTraining={startHardTraining}
                  onStartPlacementTest={startPlacementTest}
                  onShowPremium={() => uiActions.setPremiumTrigger('chapter')}
                  onBetaFeedback={() => uiActions.setShowBetaFeedback(true)}
                  onUpdateUser={(updates) => userActions.setUser(ApiClient.updateUser(updates))}
                />
              </React.Suspense>
            </PageErrorBoundary>
          )}

          {activeTab === 'exam' && (
            <ExamTab user={user} isPremium={isPremium} onStartExam={startExamWithConfidenceCheck} />
          )}

          {activeTab === 'chat' && (
            <div
              key="chat"
              className={`absolute inset-0 animate-in ${tabDirection === 'right' ? 'slide-in-from-right-2' : 'slide-in-from-left-2'} fade-in duration-200`}
            >
              <PageErrorBoundary pageName="ChatView">
                <React.Suspense fallback={<ChatSkeleton />}>
                  <ChatView user={user} />
                </React.Suspense>
              </PageErrorBoundary>
            </div>
          )}

          {activeTab === 'leaderboard' && (
            <div
              key="leaderboard"
              className={`absolute inset-0 animate-in ${tabDirection === 'right' ? 'slide-in-from-right-2' : 'slide-in-from-left-2'} fade-in duration-200`}
            >
              <PageErrorBoundary pageName="LeaderboardView">
                <React.Suspense fallback={<LeaderboardSkeleton />}>
                  <LeaderboardView user={user} />
                </React.Suspense>
              </PageErrorBoundary>
            </div>
          )}

          {activeTab === 'profile' && (
            <div
              key="profile"
              className={`animate-in ${tabDirection === 'right' ? 'slide-in-from-right-2' : 'slide-in-from-left-2'} fade-in duration-200 pb-24 overflow-y-auto`}
            >
              <PageErrorBoundary pageName="ProfileView">
                <React.Suspense fallback={<ProfileSkeleton />}>
                  <ProfileView
                    user={user}
                    onUpgrade={() => uiActions.openPaywall()}
                    onToggleDarkMode={() => {
                      const newDark = !user.isDarkMode;
                      userActions.setUser(ApiClient.updateUser({ isDarkMode: newDark }));
                      localStorage.setItem('fahryo_dark_mode', newDark ? '1' : '0');
                      document.documentElement.classList[newDark ? 'add' : 'remove']('dark');
                    }}
                    onLogout={handleLogout}
                    onOpenLegal={uiActions.setLegalModalType}
                    onUpdateUser={(updates) => userActions.setUser(ApiClient.updateUser(updates))}
                  />
                </React.Suspense>
              </PageErrorBoundary>
            </div>
          )}
        </div>
      </div>

      <BottomTabBar />

      {/* Modal Layer */}
      {showPaywall && (
        <React.Suspense fallback={null}>
          <PremiumModal trigger="general" onClose={() => uiActions.closePaywall()} />
        </React.Suspense>
      )}
      {showPremiumCelebration && (
        <PremiumCelebration onClose={() => uiActions.setShowPremiumCelebration(false)} />
      )}
      {premiumTrigger && (
        <React.Suspense fallback={null}>
          <PremiumModal
            trigger={premiumTrigger}
            context={premiumContext ?? undefined}
            onSecondaryAction={
              premiumTrigger === 'preview' ? () => uiActions.setPremiumTrigger(null) : undefined
            }
            onClose={() => uiActions.setPremiumTrigger(null)}
          />
        </React.Suspense>
      )}
      <LegalModal
        isOpen={!!legalModalType}
        type={legalModalType}
        onClose={() => uiActions.setLegalModalType(null)}
      />
      <BadgeModal
        isOpen={!!newBadge}
        badge={newBadge}
        onClose={() => userActions.setNewBadge(null)}
      />
      <InstallPrompt />
      {showConfidenceCheck && (
        <ConfidenceCheck
          onStart={startExam}
          onClose={() => uiActions.setShowConfidenceCheck(false)}
        />
      )}
      <BetaFeedbackModal
        isOpen={showBetaFeedback}
        onClose={() => uiActions.setShowBetaFeedback(false)}
        userEmail={user.email}
      />
      <ConsentBanner
        isVisible={user.consentGiven === null}
        onAccept={() => handleConsent(true)}
        onDecline={() => handleConsent(false)}
      />

      {/* Session-Resume (F-09): interrupted exam/learn session found */}
      {resumeSnapshot && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-200"
          role="alertdialog"
          aria-live="polite"
        >
          <div className="glass-strong max-w-sm w-full p-6 text-center">
            <div className="glass-pill w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <History size={26} className="text-primary" />
            </div>
            <h2 className="text-xl font-bold text-[var(--f-text)] mb-2">
              {t('session_resume_title')}
            </h2>
            <p className="text-sm text-[var(--f-text-muted)] mb-6">
              {t(
                resumeSnapshot.mode === 'exam'
                  ? 'session_resume_body_exam'
                  : 'session_resume_body_learn'
              )}
            </p>
            <button
              onClick={handleResumeSession}
              className="w-full bg-primary text-white text-sm font-semibold py-3 px-4 rounded-xl active:scale-95 transition-transform mb-2"
            >
              {t('session_resume_continue')}
            </button>
            <button
              onClick={handleDiscardSession}
              className="w-full text-sm text-[var(--f-text-muted)] hover:text-[var(--f-text)] py-2"
            >
              {t('session_resume_discard')}
            </button>
          </div>
        </div>
      )}

      {/* Toasts */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {showUpdateToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-gradient-to-r from-primary to-orange-500 text-white px-5 py-2.5 rounded-full shadow-lg text-sm font-medium animate-in slide-in-from-top-4 fade-in duration-300 flex items-center gap-2">
          <Sparkles size={15} />
          <span>{t('app_updated')}</span>
        </div>
      )}
      {/* F-20: SW update prompt — sticky banner with user-driven action.
         Replaces the previous silent auto-reload that could interrupt the
         user mid-action. */}
      {updatePromptVisible && updateApply && (
        <div
          className="glass-strong fixed bottom-24 left-3 right-3 z-50 p-4 animate-in slide-in-from-bottom-4 fade-in duration-300"
          style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
          role="alertdialog"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <Sparkles className="text-primary shrink-0 mt-0.5" size={22} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[var(--f-text)]">
                {t('update_available_title', 'Neue Version verfügbar')}
              </div>
              <div className="text-xs text-[var(--f-text-muted)] mt-0.5">
                {t(
                  'update_available_desc',
                  'Lade die neue Version, um die neuesten Verbesserungen zu erhalten.'
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    setUpdatePrompt(false);
                    updateApply();
                  }}
                  className="flex-1 bg-primary text-white text-sm font-semibold py-2 px-4 rounded-lg active:scale-95 transition-transform"
                >
                  {t('update_now', 'Jetzt aktualisieren')}
                </button>
                <button
                  onClick={() => setUpdatePrompt(false)}
                  className="px-4 text-sm text-[var(--f-text-muted)] hover:text-[var(--f-text)]"
                >
                  {t('update_later', 'Später')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <React.Suspense fallback={null}>
        <DevTools />
      </React.Suspense>
    </div>
  );
};

export default App;
